import { SystemProp, Theme, x } from '@xstyled/styled-components';
import React from 'react';
import { CSS } from '@react-spring/web';
import { Responsive, useResponsive } from '~/lib/useResponsive';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { LAYOUT_LIST } from '../itemIds';

export type Alignment = Responsive<
  | 'fill'
  | 'center'
  | 'right'
  | 'top'
  | 'bottom'
  | 'left'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright'
  | 'safeCenter'
>;

export interface Props {
  align?: Alignment;
  orientation?: Responsive<'row' | 'column' | 'row-reverse' | 'column-reverse'>;
  padding?: string;
  paddingTop?: string;
  padded?: boolean;
  wrap?: Responsive<boolean>;
  height?: SystemProp<CSS.Property.Height, Theme>;
  width?: SystemProp<CSS.Property.Width, Theme>;
  type?: string;
  card?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  overflow?: SystemProp<CSS.Property.Overflow, Theme>;
  position?: SystemProp<CSS.Property.Position, Theme>;
  tabIndex?: number;
  ariaLabel?: string;
}

const alignItemsTable = {
  fill: 'stretch',
  center: 'center',
  right: 'center',
  left: 'center',
  top: 'flex-start',
  bottom: 'flex-end',
  topleft: 'flex-start',
  topright: 'flex-start',
  bottomleft: 'flex-end',
  bottomright: 'flex-end',
  safeCenter: 'safe center'
};

const justifyContentTable = {
  fill: 'stretch',
  center: 'center',
  right: 'flex-end',
  left: 'flex-start',
  top: 'center',
  bottom: 'center',
  topleft: 'flex-start',
  topright: 'flex-end',
  bottomleft: 'flex-start',
  bottomright: 'flex-end',
  safeCenter: 'center'
};

function alignment(
  value: Alignment,
  table: typeof alignItemsTable | typeof justifyContentTable
) {
  if (typeof value === 'string') {
    return table[value];
  }

  const retVal: { [key: string]: string } = {};
  Object.keys(value).forEach((k: keyof Alignment) => {
    retVal[k] = table[value[k]];
  });
  return retVal;
}

export type LayoutListDefinition = ItemDefinition<typeof LAYOUT_LIST, Props>;

export class LayoutList extends Item<typeof LAYOUT_LIST, Props> {
  protected render({
    align = 'fill',
    orientation = 'column',
    padded = false,
    wrap = false,
    padding = '',
    paddingTop,
    width = '100%',
    height = '100%',
    type = 'div',
    overflow = 'hidden',
    card,
    position,
    tabIndex,
    ariaLabel,
    children
  }: RenderProps<Props>) {
    const wrapVal = useResponsive<boolean>(wrap);
    const orientationVal = useResponsive<
      'row' | 'column' | 'row-reverse' | 'column-reverse'
    >(orientation);

    return (
      <x.div
        w={width}
        h={height}
        display="flex"
        position={position}
        {...(tabIndex !== undefined && { tabIndex })}
        {...(ariaLabel && { role: 'region', 'aria-label': ariaLabel })}
        {...(card && {
          backgroundColor: { _: 'white', [card]: 'transparent' },
          boxShadow: { _: '0 1px 3px rgba(0,0,0,0.12)', [card]: 'none' }
        })}
      >
        <x.div
          as={type as never}
          display="flex"
          flexDirection={orientation}
          p={padded ? 'lg' : ''}
          gap={padded ? 'lg' : ''}
          minWidth="100%"
          minHeight="100%"
          overflow={overflow}
          justifyContent={alignment(
            align,
            orientation === 'column' ? alignItemsTable : justifyContentTable
          )}
          alignItems={alignment(
            align,
            orientation === 'column' ? justifyContentTable : alignItemsTable
          )}
          flexWrap={wrapVal ? 'wrap' : 'nowrap'}
          /* Keep wrapped rows packed at top to remove excessive vertical gap when only 2 rows present */
          alignContent={
            wrapVal && orientationVal === 'row' ? 'flex-start' : undefined
          }
          style={{ padding: padding, paddingTop: paddingTop }}
          aria-live={type === 'ul' ? 'polite' : 'off'}
        >
          {children}
        </x.div>
      </x.div>
    );
  }
}

registry[LAYOUT_LIST] = LayoutList;
